import { GridFilterModel } from '@mui/x-data-grid-premium'
import { createContext, PropsWithChildren, useContext, useEffect, useRef } from 'react'
import { FilterModel } from 'utils/savedFilters/validateFilterModel'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { ValidUuidString } from '@omnea-app/legacy/utils/string/isUuid'
import { TableFilterReference } from '@omnea-app/packages/universal/tables/tableFilterReference'
import { useGetTableFiltersByFilter } from '../../table-filters/useTableFilters'
import { extractUserDefinedFilters, mergeSavedFilterWithUserFilters } from '../helpers/manageSavedAndUserFilters'

export type SavedFilter = {
  id: string
  filters: FilterModel
  name: string
  reference: TableFilterReference
  isPublic: boolean
  userId: string
  deletedAt: string | null
}

export interface SavedFilterContextValue {
  savedFilter: SavedFilter | null
  isLoadingSavedFilter: boolean
  reference: TableFilterReference | undefined
  onUnselectSavedFilter(): void
}

function getInitialContextValue(): SavedFilterContextValue {
  return {
    savedFilter: null,
    isLoadingSavedFilter: false,
    reference: undefined,
    onUnselectSavedFilter: () => {},
  }
}

export const SavedFilterContext = createContext(getInitialContextValue())

export function useSavedFilterContext(): SavedFilterContextValue {
  return useContext(SavedFilterContext)
}
interface SavedFilterProviderProps extends PropsWithChildren {
  savedFilterId: ValidUuidString | null
  onUnselectSavedFilter(): void
  filterModel: GridFilterModel
  setFilterModel(filterModel: GridFilterModel): void
  reference: TableFilterReference
}

/**
 * This component is responsible for loading the saved filter from the DB and setting the filter model.
 * With the contents of the saved filter.
 *
 * It also handles clearing the saved filter from the URL when the filter model changes to something that is not a variation of the saved filter.
 */
export const SavedFilterProvider: React.FC<SavedFilterProviderProps> = ({
  children,
  savedFilterId,
  onUnselectSavedFilter,
  filterModel,
  setFilterModel,
  reference,
}) => {
  const { org } = useAuthenticatedUser()
  const storageKey = `lastAppliedSavedFilter_${reference}_${org.organisationId}`

  const { data: tableFilters, isLoading: isLoadingSavedFilter } = useGetTableFiltersByFilter(reference)
  const savedFilter = tableFilters?.find((filter) => filter?.id === savedFilterId) ?? null
  const lastAppliedFilterRef = useRef<string | null>(null)
  useEffect(() => {
    if (isLoadingSavedFilter) {
      return
    }

    if (!savedFilterId) {
      lastAppliedFilterRef.current = null
      return
    }

    if (savedFilter === null) {
      onUnselectSavedFilter()
      return
    }

    if (lastAppliedFilterRef.current === savedFilterId) {
      return
    }

    // determine whether we are switching filters or applying from a new page load
    const currentSavedFilter =
      lastAppliedFilterRef.current !== null
        ? tableFilters?.find((filter) => filter?.id === lastAppliedFilterRef.current)
        : savedFilter
    const userDefined = extractUserDefinedFilters(filterModel, currentSavedFilter)
    const mergedFilterModel = mergeSavedFilterWithUserFilters(savedFilter, userDefined)
    setFilterModel(mergedFilterModel)

    lastAppliedFilterRef.current = savedFilterId
  }, [
    savedFilterId,
    isLoadingSavedFilter,
    savedFilter,
    filterModel,
    setFilterModel,
    storageKey,
    onUnselectSavedFilter,
    tableFilters,
  ])

  return (
    <SavedFilterContext.Provider
      value={{
        savedFilter: savedFilter ? { ...savedFilter, deletedAt: null } : null,
        isLoadingSavedFilter,
        reference,
        onUnselectSavedFilter,
      }}
    >
      {children}
    </SavedFilterContext.Provider>
  )
}
